// DailyReward.js — Daily login reward popup with streak-based rewards
import React, { useState, useEffect } from 'react';

const css = `
@keyframes chestShake {
  0%,100% { transform: rotate(0deg) scale(1); }
  15%      { transform: rotate(-8deg) scale(1.05); }
  30%      { transform: rotate(8deg) scale(1.1); }
  45%      { transform: rotate(-6deg) scale(1.08); }
  60%      { transform: rotate(6deg) scale(1.05); }
  75%      { transform: rotate(-3deg) scale(1.02); }
}
@keyframes lidFly {
  0%   { transform: translateY(0) rotate(0deg) scale(1); opacity: 1; }
  100% { transform: translateY(-80px) rotate(-30deg) scale(0.5); opacity: 0; }
}
@keyframes coinRain {
  0%   { transform: translateY(-20px) rotate(0deg); opacity: 1; }
  100% { transform: translateY(60px) rotate(360deg); opacity: 0; }
}
@keyframes popIn {
  0%   { transform: scale(0.4) translateY(40px); opacity: 0; }
  70%  { transform: scale(1.08) translateY(-4px); opacity: 1; }
  100% { transform: scale(1) translateY(0); opacity: 1; }
}
@keyframes glowPulse {
  0%,100% { box-shadow: 0 0 20px rgba(255,214,0,0.4); }
  50%      { box-shadow: 0 0 48px rgba(255,214,0,0.9); }
}
@keyframes badgePop {
  0%   { transform: scale(0) rotate(-20deg); opacity: 0; }
  70%  { transform: scale(1.2) rotate(5deg); opacity: 1; }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
}
@keyframes mysteryReveal {
  0%   { filter: blur(12px); transform: scale(0.6); opacity: 0; }
  100% { filter: blur(0); transform: scale(1); opacity: 1; }
}
.daily-reward-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.82);
  z-index: 9990;
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
  font-family: 'Nunito', sans-serif;
}
.daily-reward-card {
  background: linear-gradient(160deg, #0D1B4B 0%, #1A0533 60%, #0A0A2E 100%);
  border: 2px solid rgba(255,214,0,0.5);
  border-radius: 28px;
  padding: 32px 28px 24px;
  max-width: 360px;
  width: 100%;
  text-align: center;
  animation: popIn 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards;
  position: relative;
  overflow: hidden;
}
.daily-reward-glow {
  animation: glowPulse 2s ease-in-out infinite;
}
.chest-wrap { font-size: 72px; margin: 0 0 8px; display: block; cursor: pointer; }
.chest-wrap.shaking { animation: chestShake 0.6s ease-in-out; }
.chest-lid {
  display: inline-block;
  font-size: 36px;
  position: absolute;
  top: 0; left: 50%;
  transform: translateX(-50%);
}
.chest-lid.flying { animation: lidFly 0.6s ease-in forwards; }
.coin-rain-wrap { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
.coin-rain-item {
  position: absolute;
  font-size: 18px;
  animation: coinRain 0.9s ease-in forwards;
}
.badge-reveal { animation: badgePop 0.5s cubic-bezier(0.34,1.56,0.64,1) 0.3s both; }
.mystery-reveal { animation: mysteryReveal 0.6s ease-out 0.2s both; }
.claim-btn {
  background: linear-gradient(135deg, #FFD600, #FF9500);
  border: none;
  border-radius: 16px;
  padding: 14px 32px;
  font-size: 17px;
  font-weight: 900;
  color: #1A1A2E;
  cursor: pointer;
  font-family: 'Nunito', sans-serif;
  width: 100%;
  margin-top: 20px;
  box-shadow: 0 6px 20px rgba(255,150,0,0.45);
  transition: transform 0.1s;
}
.claim-btn:active { transform: scale(0.97); }
.streak-day-row {
  display: flex; gap: 6px; justify-content: center;
  margin: 12px 0 4px; flex-wrap: wrap;
}
.streak-day-dot {
  width: 28px; height: 28px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 900;
}
`;

const REWARDS = [
  { day: 1,  coins: 10,  title: 'Welcome back!',           emoji: '🌟',  badge: null,              special: null },
  { day: 2,  coins: 15,  title: 'Day 2 — Nice!',           emoji: '✨',  badge: null,              special: 'mystery' },
  { day: 3,  coins: 20,  title: 'Explorer Badge!',         emoji: '🧭',  badge: 'Explorer',        special: null },
  { day: 4,  coins: 25,  title: 'Keep it up!',             emoji: '💪',  badge: null,              special: null },
  { day: 5,  coins: 30,  title: 'Explorer Pack Unlocked!', emoji: '🎒',  badge: null,              special: 'pack' },
  { day: 6,  coins: 40,  title: 'Almost there!',           emoji: '🔥',  badge: null,              special: null },
  { day: 7,  coins: 50,  title: 'Weekly Warrior Chest!',   emoji: '💎',  badge: 'Warrior',         special: 'chest' },
  { day: 14, coins: 75,  title: 'NeevStar Pioneer!',       emoji: '🚀',  badge: 'Pioneer',         special: 'title', titleName: 'NeevStar Pioneer' },
  { day: 30, coins: 150, title: 'LEGEND status!',          emoji: '👑',  badge: 'Legend',          special: 'legend' },
];

const MYSTERY_EMOJIS = ['🦄','🌈','🎯','⚡','🎪','🔮','🌊','🏔️'];

function getReward(streak) {
  // Find the best matching reward tier
  const tiers = [30, 14, 7, 5, 3, 2, 1];
  for (const t of tiers) {
    if (streak >= t) return REWARDS.find(r => r.day === t) || REWARDS[0];
  }
  return REWARDS[0];
}

export default function DailyReward({ streak, onClaim, onClose }) {
  const [phase, setPhase] = useState('show');   // show | opening | opened | claimed
  const [shaking, setShaking] = useState(false);
  const [lidFlying, setLidFlying] = useState(false);
  const [coins, setCoins] = useState([]);
  const [mystery, setMystery] = useState(null);
  const [autoTimer, setAutoTimer] = useState(null);

  const reward = getReward(streak);
  const isChest = reward.special === 'chest' || reward.special === 'legend';

  // Inject CSS once
  useEffect(() => {
    if (!document.getElementById('daily-reward-css')) {
      const s = document.createElement('style');
      s.id = 'daily-reward-css';
      s.textContent = css;
      document.head.appendChild(s);
    }
    // Auto-dismiss after 8 seconds if not interacted
    const t = setTimeout(() => handleClaim(), 8000);
    setAutoTimer(t);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChestTap = () => {
    if (phase !== 'show') return;
    clearTimeout(autoTimer);
    setShaking(true);
    setTimeout(() => {
      setShaking(false);
      if (isChest) {
        setLidFlying(true);
        // Rain coins
        const c = Array.from({ length: 12 }, (_, i) => ({
          id: i,
          left: `${10 + Math.random() * 80}%`,
          delay: `${Math.random() * 0.4}s`,
        }));
        setCoins(c);
      }
      if (reward.special === 'mystery') {
        setMystery(MYSTERY_EMOJIS[Math.floor(Math.random() * MYSTERY_EMOJIS.length)]);
      }
      setPhase('opened');
    }, 650);
  };

  const handleClaim = () => {
    clearTimeout(autoTimer);
    setPhase('claimed');
    onClaim(reward.coins, reward.badge);
    setTimeout(() => onClose(), 600);
  };

  // Streak progress dots (show last 7 days)
  const dotCount = Math.min(streak, 7);

  return (
    <div className="daily-reward-overlay" onClick={e => e.target.classList.contains('daily-reward-overlay') && handleClaim()}>
      <div className={`daily-reward-card daily-reward-glow`}>

        {/* Coin rain */}
        {coins.length > 0 && (
          <div className="coin-rain-wrap">
            {coins.map(c => (
              <span key={c.id} className="coin-rain-item"
                style={{ left: c.left, top: '30%', animationDelay: c.delay }}>
                🪙
              </span>
            ))}
          </div>
        )}

        {/* Day label */}
        <p style={{ fontSize: 11, fontWeight: 900, color: 'rgba(255,214,0,0.7)',
          letterSpacing: '0.1em', margin: '0 0 4px' }}>
          DAY {streak} LOGIN REWARD
        </p>

        {/* Title */}
        <h2 style={{ fontSize: 22, fontWeight: 900, color: 'white', margin: '0 0 16px', lineHeight: 1.3 }}>
          {reward.title}
        </h2>

        {/* Main visual */}
        <div style={{ position: 'relative', minHeight: 90, marginBottom: 8 }}>
          {phase === 'show' ? (
            <span
              className={`chest-wrap ${shaking ? 'shaking' : ''}`}
              onClick={handleChestTap}
              title="Tap to open!"
            >
              {isChest ? '📦' : reward.emoji}
            </span>
          ) : (
            <div style={{ position: 'relative', display: 'inline-block' }}>
              {isChest && (
                <>
                  <span style={{ fontSize: 72 }}>📦</span>
                  <span className={`chest-lid ${lidFlying ? 'flying' : ''}`}>🔓</span>
                </>
              )}
              {!isChest && (
                <span style={{ fontSize: 72, display: 'block' }}>{reward.emoji}</span>
              )}
            </div>
          )}
        </div>

        {/* Tap hint */}
        {phase === 'show' && (
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', fontWeight: 700, margin: '0 0 12px' }}>
            👆 Tap to open!
          </p>
        )}

        {/* Revealed content */}
        {phase === 'opened' && (
          <div style={{ marginBottom: 8 }}>
            {/* Coins */}
            <div className="badge-reveal" style={{
              background: 'rgba(255,214,0,0.15)',
              border: '1.5px solid rgba(255,214,0,0.4)',
              borderRadius: 14, padding: '10px 20px', marginBottom: 8,
              display: 'inline-flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ fontSize: 22 }}>🪙</span>
              <span style={{ fontSize: 22, fontWeight: 900, color: '#FFD600' }}>+{reward.coins} NeevCoins</span>
            </div>

            {/* Badge */}
            {reward.badge && (
              <div className="badge-reveal" style={{ marginBottom: 6 }}>
                <div style={{
                  background: 'linear-gradient(135deg, #8B2BE2, #B44FFF)',
                  borderRadius: 12, padding: '6px 16px',
                  display: 'inline-block',
                }}>
                  <span style={{ fontSize: 13, fontWeight: 900, color: 'white' }}>
                    🏅 {reward.badge} Badge Unlocked!
                  </span>
                </div>
              </div>
            )}

            {/* Mystery emoji */}
            {mystery && (
              <div className="mystery-reveal" style={{ fontSize: 36, marginBottom: 4 }}>
                {mystery}
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', fontWeight: 700, margin: '2px 0 0' }}>
                  Mystery bonus!
                </p>
              </div>
            )}
          </div>
        )}

        {/* Streak dots */}
        <div className="streak-day-row">
          {Array.from({ length: 7 }, (_, i) => {
            const dayNum = i + 1;
            const done = dayNum <= dotCount;
            const isToday = dayNum === dotCount;
            return (
              <div key={i} className="streak-day-dot" style={{
                background: done
                  ? isToday
                    ? 'linear-gradient(135deg,#FFD600,#FF9500)'
                    : 'rgba(255,214,0,0.4)'
                  : 'rgba(255,255,255,0.08)',
                border: isToday ? '2px solid #FFD600' : '1px solid rgba(255,255,255,0.1)',
                color: done ? (isToday ? '#1A1A2E' : '#FFD600') : 'rgba(255,255,255,0.3)',
              }}>
                {dayNum}
              </div>
            );
          })}
        </div>
        <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', fontWeight: 700, margin: '4px 0 0' }}>
          🔥 {streak}-day streak
        </p>

        {/* Claim button */}
        <button className="claim-btn" onClick={handleClaim}>
          {phase === 'show' ? `Claim ${reward.coins} Coins! 🪙` : '✅ Awesome! Claim Reward!'}
        </button>

        {/* Skip */}
        <button onClick={handleClaim} style={{
          background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)',
          fontSize: 12, fontWeight: 700, cursor: 'pointer',
          marginTop: 8, fontFamily: "'Nunito', sans-serif",
        }}>
          skip
        </button>
      </div>
    </div>
  );
}

/**
 * Call this after login to check if daily reward should show.
 * Returns true if it should show (new day login).
 */
export function shouldShowDailyReward(userEmail) {
  const key = `ns_daily_reward_${userEmail}_${new Date().toDateString()}`;
  if (localStorage.getItem(key)) return false;
  return true;
}

export function markDailyRewardShown(userEmail) {
  const key = `ns_daily_reward_${userEmail}_${new Date().toDateString()}`;
  localStorage.setItem(key, '1');
}
